import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Crown, TrendingUp, CheckCircle } from 'lucide-react';
import { getPlanCommercialName } from '@/constants/plans';
import { formatDate } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface CurrentPlanCardProps {
  planType: string;
  billingPeriod?: string | null;
  expiresAt?: string | null;
  onUpgradeClick?: () => void;
  showUpgradeButton?: boolean;
}

const formatBillingPeriod = (period: string | null | undefined): string => {
  if (!period) return 'Gratuito';
  switch (period) {
    case 'weekly': return 'Semanal';
    case 'monthly': return 'Mensal';
    case 'quarterly': return 'Trimestral';
    case 'yearly': return 'Anual';
    default: return period;
  }
};

export const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  planType,
  billingPeriod,
  expiresAt,
  onUpgradeClick,
  showUpgradeButton = false
}) => {
  const commercialName = getPlanCommercialName(planType);
  const isFree = planType.includes('free');
  const isInfinity = planType.includes('premium');
  
  const formattedExpiryDate = expiresAt 
    ? formatDate(new Date(expiresAt), "d 'de' MMMM 'de' yyyy", { locale: ptBR })
    : null;

  const isExpiringSoon = expiresAt && new Date(expiresAt) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  return (
    <Card className="bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm relative overflow-hidden">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-luxury-pinkBright/5 to-transparent pointer-events-none"></div>
      
      <CardHeader className="pb-4 relative z-10">
        <CardTitle className="text-white text-xl font-semibold flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span>Plano Atual</span>
            {!isFree && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle className="h-3 w-3 mr-1" />
                Ativo
              </Badge>
            )}
          </div>
          {isInfinity && (
            <Badge className="bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30">
              <Crown className="h-3 w-3 mr-1" />
              Infinity
            </Badge>
          )}
        </CardTitle>
        <div className="mt-2">
          <div className="text-3xl font-bold text-luxury-pinkBright">
            {commercialName}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-white/60">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-luxury-pinkBright" />
            <span className="text-white">
              {formatBillingPeriod(billingPeriod)}
            </span>
          </div>
          
          {formattedExpiryDate && (
            <div className={`flex items-center gap-2 ${isExpiringSoon ? 'text-red-400' : 'text-white/60'}`}>
              <Clock className="h-4 w-4" />
              <span>Expira em {formattedExpiryDate}</span>
            </div>
          )}
        </div>

        {showUpgradeButton && !isInfinity && (
          <Button
            onClick={onUpgradeClick}
            className="w-full bg-gradient-to-b from-[#C4406B] to-[#9F3764] hover:from-[#D4507B] hover:to-[#AF4774] text-white font-semibold shadow-lg"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            {isFree ? 'Seja Premium Agora!' : 'Fazer Upgrade'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
